import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save } from "lucide-react";

const PRIORITY_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "importante", label: "Importante" },
  { value: "urgente", label: "Urgente" },
  { value: "monitorar", label: "Monitorar" },
];

export const STATUS_LABELS = {
  entrou: "Veículo Entrou",
  em_inspecao: "Em Inspeção",
  aguardando_estimate: "Aguardando Estimate",
  aguardando_aprovacao: "Aguardando Aprovação",
  aguardando_pecas: "Aguardando Peças",
  em_andamento: "Em Andamento",
  pronto: "Pronto",
  entregue: "Entregue",
  novo: "Novo",
  aprovado: "Aprovado",
  em_reparo: "Em Reparo",
  pintura: "Pintura",
  montagem: "Montagem",
  quality_check: "Quality Check",
  pronto_entrega: "Pronto p/ Entrega",
  recebido: "Recebido",
  estimate: "Estimate",
  kit_enviado: "Kit Enviado",
  aguardando_pagamento: "Aguardando Pagamento",
  agendado: "Agendado",
};

export const STATUSES_BY_TYPE = {
  mechanic: ["entrou", "em_inspecao", "aguardando_estimate", "aguardando_aprovacao", "aguardando_pecas", "em_andamento", "pronto", "entregue"],
  body_shop: ["novo", "em_inspecao", "aguardando_aprovacao", "aprovado", "aguardando_pecas", "em_reparo", "pintura", "montagem", "quality_check", "pronto_entrega", "entregue"],
  warranty: ["entrou", "agendado", "recebido", "em_inspecao", "estimate", "kit_enviado", "aguardando_aprovacao", "aprovado", "em_reparo", "pronto", "entregue", "aguardando_pagamento"],
};

export default function EditarJobDialog({ jobId, open, onOpenChange, onSaved }) {
  const [job, setJob] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !jobId) {
      setJob(null);
      setForm(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        setJob(data.job);
        setForm({
          service_type: data.job.service_type || "",
          technician: data.job.technician || "",
          priority: data.job.priority || "normal",
          notes: data.job.notes || "",
          client_note: data.job.client_note || "",
          status: data.job.status || "entrou",
          job_type: data.job.job_type || "mechanic",
        });
      } catch { toast.error("Erro ao carregar OS"); }
    })();
  }, [open, jobId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_type?.trim()) return toast.error("Tipo de serviço é obrigatório");
    setSaving(true);
    try {
      await api.put(`/jobs/${jobId}`, form);
      toast.success("OS atualizada");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const statuses = form ? STATUSES_BY_TYPE[form.job_type] || STATUSES_BY_TYPE.mechanic : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-job-dialog">
        <DialogHeader>
          <DialogTitle>Editar Ordem de Serviço</DialogTitle>
          <DialogDescription>
            {job ? <span className="font-mono text-xs">OS #{job.id.slice(-6).toUpperCase()}</span> : "Carregando…"}
          </DialogDescription>
        </DialogHeader>
        {!form ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Carregando…</div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Tipo de Serviço *</Label>
              <Input
                value={form.service_type}
                onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                data-testid="edit-job-service-type"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Técnico</Label>
                <Input
                  value={form.technician}
                  onChange={(e) => setForm({ ...form, technician: e.target.value })}
                  data-testid="edit-job-technician"
                />
              </div>
              <div>
                <Label>Prioridade</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="edit-job-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo de Fluxo</Label>
                <Select
                  value={form.job_type}
                  onValueChange={(v) => setForm({ ...form, job_type: v, status: STATUSES_BY_TYPE[v][0] })}
                >
                  <SelectTrigger data-testid="edit-job-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mechanic">Mecânica</SelectItem>
                    <SelectItem value="body_shop">Body Shop</SelectItem>
                    <SelectItem value="warranty">Garantia Estendida</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="edit-job-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {statuses.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notas / Reclamação <span className="text-[10px] text-muted-foreground font-normal ml-1">(interno)</span></Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="edit-job-notes"
              />
            </div>
            <div className="border-t border-border pt-3">
              <Label className="text-primary">
                Mensagem ao Cliente
                <span className="text-[10px] text-muted-foreground font-normal ml-1">(visível no Portal do Cliente)</span>
              </Label>
              <Textarea
                rows={3}
                value={form.client_note}
                onChange={(e) => setForm({ ...form, client_note: e.target.value })}
                placeholder="Ex: Comprei essa peça no dealer, demora 5 dias para chegar."
                data-testid="edit-job-client-note"
                className="border-primary/40 focus-visible:border-primary"
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Esta mensagem aparece diretamente no Portal do Cliente, ao lado do status atual da OS. Deixe em branco para não exibir.
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="edit-job-save">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? "Salvando…" : "Salvar"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
